import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { TableVirtuoso, TableVirtuosoHandle } from 'react-virtuoso';
import { useAppContext } from 'src/contexts/AppContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui/Stack';
import { ChevronDownIconV2 } from 'src/svgs/Viagogo/goicons/ChevronDownIconV2';
import { ChevronRightIconV3 } from 'src/svgs/Viagogo/goicons/ChevronRightIconV3';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import { GroupType } from 'src/WebApiController';

import { itemSizeDefault } from '../Table/Table.utils';
import { GroupHeaderRow } from './components/GroupHeaderRow';
import * as styles from './components/Table.css';
import { TableProvider } from './components/TableContext';
import { TablePagination } from './components/TablePagination';
import { VirtuosoTable } from './components/VirtuosoTable';
import { VirtuosoTableContext } from './components/VirtuosoTableContext';
import { VirtuosoTableFooter } from './components/VirtuosoTableFooter';
import { VirtuosoTableHead } from './components/VirtuosoTableHead';
import { VirtuosoTableRow } from './components/VirtuosoTableRow';
import * as groupStyles from './GroupedTable.css';
import { useTableSetup } from './hooks/useTableSetup';
import {
  RowWrapper,
  TablePaginationConfig,
  TableRowHeightVariant,
} from './types';

const virtuosoTableOverscan = { main: 600, reverse: 600 };
const virtuosoTableStyle = { width: '100%', height: '100%' };

const memoizedVirtuosoComponents = {
  Table: VirtuosoTable,
  TableHead: VirtuosoTableHead,
  TableRow: VirtuosoTableRow,
  TableFoot: VirtuosoTableFooter,
};

const TableContainerWidth = '--table-container-width';

interface GroupedVirtuosoTableV2Props<T = any> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  variant?: TableRowHeightVariant;
  bordered?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: T) => string | null | undefined;
  getItemId?: (data: T) => string;
  getRowId?: (data: T, index: number) => string;
  isSelectionDisabled?: (data: T) => string | undefined;
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void;
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableSubRowSelection?: boolean | ((row: Row<T>) => boolean);
  onRowShiftSelect?: (
    tableRows: Row<T | null>[] | undefined,
    row: Row<T | null>,
    index: number
  ) => void;
  pagination?: TablePaginationConfig;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  summaryData?: T | null;
  style?: React.CSSProperties;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  footerCta?: React.ReactNode;
  rowWrapper?: RowWrapper<T>;
  renderGroupHeader?: (groupId: string, groupData: T[]) => React.ReactNode;
  getSubRows?: (row: T) => T[];
  isGroupRow?: (data: T) => boolean;
}

export const GroupedVirtuosoTable = <T extends Record<string, any>>({
  actionColId,
  data,
  columns,
  variant = 'normal',
  alwaysShowCheckbox,
  getGroupId,
  getRowId,
  getItemId,
  isSelectionDisabled,
  onRowSelectionChange,
  onExpandedChange,
  onPaginationChange,
  onSortingChange,
  enableRowSelection,
  enableMultiRowSelection,
  enableSubRowSelection,
  onRowShiftSelect,
  pagination,
  className,
  onRowClick,
  summaryData,
  state,
  sortingFns,
  meta,
  footerCta,
  rowWrapper,
  renderGroupHeader,
  getSubRows,
  isGroupRow,
}: GroupedVirtuosoTableV2Props<T>) => {
  const { mainRoute } = useAppContext();
  const inFullPageEventView = isInFullPageEventView(mainRoute);

  const {
    table,
    tableColumns,
    renderHeaderGroups,
    renderRowContent,
    showCheckbox,
  } = useTableSetup({
    actionColId,
    data,
    columns,
    alwaysShowCheckbox,
    getGroupId,
    getItemId,
    isSelectionDisabled,
    onRowSelectionChange,
    onExpandedChange,
    onPaginationChange,
    onSortingChange,
    enableRowSelection,
    enableMultiRowSelection,
    enableSubRowSelection,
    pagination,
    summaryData,
    state,
    sortingFns,
    meta,
    getSubRows,
    isGroupRow,
    getRowId,
    getRowCanExpand: isGroupRow
      ? (row) => Boolean(isGroupRow(row.original))
      : undefined,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<TableVirtuosoHandle>(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [isScrolledToRight, setIsScrolledToRight] = React.useState(false);

  // Measure container width for group headers
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          const width = Math.round(entry.contentRect.width);
          element.style.setProperty(TableContainerWidth, `${width}px`);
        }
      });
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Track overflow for hiding pinned column shadow
  useEffect(() => {
    const wrapper = scrollContainerRef.current;
    if (!wrapper) return;

    // Find the virtuoso scroller element
    const container = wrapper.querySelector(
      '[data-virtuoso-scroller]'
    ) as HTMLElement;
    if (!container) return;

    const checkOverflow = () => {
      const overflow = container.scrollWidth > container.clientWidth;
      if (overflow !== hasOverflow) {
        setHasOverflow(overflow);
      }
    };

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const isAtRight = scrollLeft + clientWidth >= scrollWidth - 1;
      if (isAtRight !== isScrolledToRight) {
        setIsScrolledToRight(isAtRight);
      }
    };

    const handleResize = () => {
      checkOverflow();
      checkScroll();
    };

    // Wait for virtuoso to mount
    const timer = setTimeout(() => {
      handleResize();
      container.addEventListener('scroll', checkScroll);
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);
    }, 100);

    return () => {
      clearTimeout(timer);
      container.removeEventListener('scroll', checkScroll);
    };
  }, [hasOverflow, isScrolledToRight]);

  // Get all visible rows (TanStack automatically includes/excludes children based on expanded state)
  const allTableRows = table.getRowModel().rows;

  // Separate and reorder rows: ungrouped first, then grouped
  const tableRows = useMemo(() => {
    const ungrouped: typeof allTableRows = [];
    const grouped: typeof allTableRows = [];

    allTableRows.forEach((row) => {
      if (row.depth === 0) {
        if (isGroupRow?.(row.original)) {
          grouped.push(row);
        } else {
          ungrouped.push(row);
        }
      } else {
        grouped.push(row);
      }
    });
    return [...ungrouped, ...grouped];
  }, [allTableRows, isGroupRow]);

  // Render row content - differentiate between group headers and regular rows
  const renderRowContentWithGrouping = useCallback(
    (index: number, row: Row<T>) => {
      const isParentRow = row.depth === 0 && isGroupRow?.(row.original);

      if (isParentRow) {
        // Render as group header
        const groupId = getItemId?.(row.original) ?? row.id;
        const childData = row.subRows?.map((r) => r.original) || [];
        const isExpanded = row.getIsExpanded();

        return (
          <GroupHeaderRow
            groupId={groupId}
            childData={childData}
            isExpanded={isExpanded}
            toggleExpanded={() => row.toggleExpanded()}
            selected={row.getIsSelected()}
            renderGroupHeader={renderGroupHeader}
            showCheckbox={showCheckbox}
            parentRow={row}
            table={table}
            getGroupId={getGroupId}
            getItemId={getItemId}
            isSelectionDisabled={isSelectionDisabled}
            tableContainerWidth={TableContainerWidth}
          />
        );
      }

      // Regular data row
      const cellContent = renderRowContent(row);
      const wrappedContent = rowWrapper
        ? rowWrapper(row.original, cellContent, index)
        : cellContent;

      return wrappedContent;
    },
    [
      isGroupRow,
      getItemId,
      tableColumns.length,
      renderGroupHeader,
      renderRowContent,
      rowWrapper,
    ]
  );

  const virtuosoContext = useMemo<VirtuosoTableContext<T>>(() => {
    return {
      table,
      tableRows,
      variant,
      renderRowContent: renderRowContentWithGrouping,
      onRowClick,
      onRowShiftSelect,
      isGroupRow,
    };
  }, [
    table,
    tableRows,
    variant,
    renderRowContentWithGrouping,
    onRowClick,
    onRowShiftSelect,
    isGroupRow,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: inFullPageEventView ? '100%' : undefined,
      }}
    >
      <TableProvider value={table}>
        <div
          ref={scrollContainerRef}
          className={clsx(
            styles.tableContainer,
            styles.tableBordered,
            styles.POSTableVirtuosoContainer,
            (!hasOverflow || isScrolledToRight) && styles.noOverflow,
            className
          )}
        >
          <TableVirtuoso
            ref={virtuosoRef}
            style={virtuosoTableStyle}
            data={tableRows}
            overscan={virtuosoTableOverscan}
            context={virtuosoContext}
            components={memoizedVirtuosoComponents}
            fixedHeaderContent={renderHeaderGroups}
            itemContent={renderRowContentWithGrouping}
            itemSize={itemSizeDefault}
          />
        </div>
      </TableProvider>

      {pagination && (
        <Stack
          justifyContent="spaceBetween"
          style={{ marginTop: vars.spacing.m }}
        >
          <TablePagination
            table={table}
            pageSize={pagination.pageSize}
            variant={pagination.variant}
          />
          {footerCta}
        </Stack>
      )}
    </div>
  );
};
